<template>
  <LeadPill
    :tone="tone === 'purple' ? 'neutral' : tone"
    :class="tone === 'purple' ? PURPLE_OVERRIDE : undefined"
    dot
    :pulse="pulse"
    data-testid="status-indicator"
    :data-status="status">
    {{ label ?? translator.get(`order.status.${status}`) }}
  </LeadPill>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import LeadPill, { type LeadPillTone } from '@/components/lead-design/LeadPill.vue';
import type { Order } from '@/types/types';
import translator from '@/locale/translator';

export type LeadStatusPillTone = LeadPillTone | 'purple';
// Order['lead_order_status'] is optional + nullable; the pill props tolerate that
// (falls back to 'danger'), but the STATUS_TONE lookup table only needs the
// concrete named states as keys.
export type OrderStatus = Order['lead_order_status'] | 'draft';
type KnownOrderStatus = Exclude<OrderStatus, null | undefined>;

const props = defineProps<{
  status: OrderStatus;
  /** Override the auto-translated label. */
  label?: string;
}>();

// Purple is not in the LEAD design system but is kept for backwards compatibility
// with the legacy StatusIndicator (used by `confirmed` orders).
const PURPLE_OVERRIDE = 'tw:bg-purple-50! tw:border-purple-200! tw:text-purple-600!';

const STATUS_TONE: Record<KnownOrderStatus, LeadStatusPillTone> = {
  draft: 'neutral',
  to_be_confirmed: 'warning',
  confirmed: 'purple',
  running: 'success',
  waiting_to_finish: 'warning',
  finished: 'primary',
  unknown: 'danger',
};

const tone = computed<LeadStatusPillTone>(() => (props.status ? STATUS_TONE[props.status] : 'danger') ?? 'danger');
// Only "running" is genuinely live — the rest are steady states.
const pulse = computed(() => props.status === 'running');
</script>
